import { FC, Fragment } from 'react';
import { Link } from 'react-router-dom';

import IconExpectation1 from '@/assets/icons/icon_expectation_001.svg';
import IconExpectation2 from '@/assets/icons/icon_expectation_002.svg';
import IconExpectation3 from '@/assets/icons/icon_expectation_003.svg';
import ImageTable from '@/assets/images/guide/img_table_race_001_pc.png';
import ImageTableWebp from '@/assets/images/guide/img_table_race_001_pc.png?webp';
import ImageTable2x from '@/assets/images/guide/img_table_race_001_pc@2x.png';
import ImageTable2xWebp from '@/assets/images/guide/img_table_race_001_pc@2x.png?webp';
import ImageTable3x from '@/assets/images/guide/img_table_race_001_pc@3x.png';
import ImageTable3xWebp from '@/assets/images/guide/img_table_race_001_pc@3x.png?webp';
import ImageTableResultPC from '@/assets/images/guide/img_table_result_pc.png';
import ImageTableResultPCWebp from '@/assets/images/guide/img_table_result_pc.png?webp';
import ImageTableResultPC2x from '@/assets/images/guide/img_table_result_pc@2x.png';
import ImageTableResultPC2xWebp from '@/assets/images/guide/img_table_result_pc@2x.png?webp';
import ImageTableResultPC3x from '@/assets/images/guide/img_table_result_pc@3x.png';
import ImageTableResultPC3xWebp from '@/assets/images/guide/img_table_result_pc@3x.png?webp';
import Image from '@/components/elements/Image';
import { GuidePageTitles } from '@/pages/Guide/types';

import { Container, ContentBox, Title } from '../../style';
import {
  DList,
  ExpectationList,
  IconImage,
  ImageWrapper,
  RecentResultWrapper,
  SmallText,
} from '../style';

const GuideRaceCardPcTemplate: FC = () => {
  const descriptions: { title: string; content: JSX.Element }[] = [
    {
      title: '1. 予想印',
      content: (
        <>
          予想家による評価を印にしたもの。
          <br />
          <ExpectationList>
            <li>
              <IconImage src={IconExpectation1} />
              本命
            </li>
            <li>
              <IconImage src={IconExpectation2} />
              対抗
            </li>
            <li>
              <IconImage src={IconExpectation3} />
              単穴
            </li>
          </ExpectationList>
        </>
      ),
    },
    {
      title: '2. 枠番',
      content: (
        <>
          選手は車番でグループに分けられ「枠番」という番号を付けられる。車立によって各枠の人数が変わる。
        </>
      ),
    },
    {
      title: '3. 選手名など',
      content: (
        <>
          選手の基本情報。
          <br />
          <b>選手名（年齢） 登録府県 / 競輪学校卒業期 / 級班 / 競走得点</b>
          <br />
          の順で情報を表示。
        </>
      ),
    },
    {
      title: '4. 脚質',
      content: (
        <>
          選手の脚のタイプのこと。
          <br />
          <Link to="/glossary/き/脚質">詳しくはこちら</Link>
        </>
      ),
    },
    {
      title: '5. ギア倍数',
      content: (
        <>
          選手がレースで使用するギア倍数。
          <br />
          <Link to="/glossary/き/ギア倍数">詳しくはこちら</Link>
        </>
      ),
    },
    {
      title: '6. 決まり手',
      content: (
        <>
          連対した際に取った戦法。
          <br />
          <Link to="/glossary/き/決まり手">詳しくはこちら</Link>
        </>
      ),
    },
    {
      title: '7. S（スタンディング）・B（バック）',
      content: (
        <>
          スタンディング、バックを取った回数。
          <br />
          <SmallText>
            スタンディング：発走時に一番最初に前へ出て誘導員の後ろにつくこと。
            <br />
            バック：最終周回のバックストレッチラインを先頭で通過すること。
          </SmallText>
        </>
      ),
    },
    {
      title: '8. 勝率',
      content: <>直近4ヶ月間の勝率、2連対率、3連対率。</>,
    },
    {
      title: '9. 着順',
      content: <>直近4ヶ月間の1着・2着・3着・着外（4着以降）の回数。</>,
    },
    {
      title: '10. 並び予想',
      content: (
        <>
          選手が形成するライン。
          <br />
          <Link to="/glossary/ら/ライン">詳しくはこちら</Link>
        </>
      ),
    },
  ];

  return (
    <Container>
      <ContentBox>
        <div>
          <Title>{GuidePageTitles.RaceCard}</Title>
          <ImageWrapper>
            <Image
              srcSet={`${ImageTable} 1x, ${ImageTable2x} 2x, ${ImageTable3x} 3x`}
              mediaSet={[
                {
                  srcSet: `${ImageTableWebp} 1x, ${ImageTable2xWebp} 2x, ${ImageTable3xWebp} 3x`,
                  type: 'image/webp',
                },
              ]}
              alt="出走表（予想印・枠番・選手名）のイメージ"
              aspectRatio={292 / 712}
            />
          </ImageWrapper>
          <DList>
            {descriptions.map(d => (
              <Fragment key={d.title}>
                <dt>{d.title}</dt>
                <dd>{d.content}</dd>
              </Fragment>
            ))}
          </DList>
        </div>
        <RecentResultWrapper>
          <Title>直近成績の見方</Title>
          <ImageWrapper>
            <Image
              srcSet={`${ImageTableResultPC} 1x, ${ImageTableResultPC2x} 2x, ${ImageTableResultPC3x} 3x`}
              mediaSet={[
                {
                  srcSet: `${ImageTableResultPCWebp} 1x, ${ImageTableResultPC2xWebp} 2x, ${ImageTableResultPC3xWebp} 3x`,
                  type: 'image/webp',
                },
              ]}
              alt="出走表（勝率・着順）のイメージ"
              aspectRatio={206 / 712}
            />
          </ImageWrapper>
          <DList>
            <dt>11. 直近成績</dt>
            <dd>選手が出場した直近3開催の成績。</dd>
          </DList>
        </RecentResultWrapper>
      </ContentBox>
    </Container>
  );
};

export default GuideRaceCardPcTemplate;
